import { createSlice } from "@reduxjs/toolkit";
import { IDirectoryResponse } from "../../api/types/directory/IResponse";
import { IHotel } from "../../api/types/directory/IHotel";
import { IOperator } from "../../api/types/directory/IOperator";
import { ITourFilter } from "../../api/types/directory/ITourFilter";
import { IReportData, IReportInfo } from "../../api/types/reports/IReportInfo";
import { fetchDirectoryDataForReportInfo } from "../directory/thunks/fetchDirectoryDataForReportInfo";
import { fetchDirectoryDataForTicketGridInfo } from "../directory/thunks/fetchDirectoryDataForTicketGridInfo";
import { closeModal } from "../modals/modalsSlice";
import { editReportTemplate } from "../reports/thunks/editReportTemplate";

interface IReportDataState {
  data?: IReportData;
  id: number;
}

const initialState: IReportDataState = {
  id: 0,
};

function mapOperatorsToReportData(
  info: IReportInfo,
  operators?: IOperator[],
): IOperator[] {
  if (!operators) {
    return [];
  }

  return info.operators
    .split(",")
    .map((id) => operators.find((o) => `${o.id}` === id))
    .filter((v) => v !== undefined) as IOperator[];
}

function buildReportDataFromDirectory(
  info: IReportInfo,
  payload: IDirectoryResponse,
): IReportData {
  const data: IReportData = {
    ...info,
    sOperators: mapOperatorsToReportData(info, payload.operators),
    sTourFilter: [],
    sHotel: [],
  };

  const tours = payload.tourFilters;
  const hotels = payload.hotels;
  if (tours) {
    data.sTourFilter = info.tour_id
      .split(",")
      .map((id) => tours.find((o) => `${o.id}` === id))
      .filter((v) => v !== undefined) as ITourFilter[];
  }
  if (hotels) {
    data.sHotel = info.hotels
      .split(",")
      .map((id) => hotels.find((o) => `${o.id}` === id))
      .filter((v) => v !== undefined) as IHotel[];
  }

  return data;
}

const reportDataSlice = createSlice({
  name: "reportData",
  initialState,
  reducers: {
    resetReportData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchDirectoryDataForReportInfo.fulfilled,
      (state, action) => {
        const info = action.meta.arg;
        state.data = buildReportDataFromDirectory(info, action.payload);
        state.id = info.id;
      },
    );
    builder.addCase(
      fetchDirectoryDataForTicketGridInfo.fulfilled,
      (state, action) => {
        const info = action.meta.arg;
        state.data = {
          ...info,
          sOperators: mapOperatorsToReportData(info, action.payload.operators),
          sTourFilter: [],
          sHotel: [],
        };
        state.id = info.id;
      },
    );
    builder.addCase(closeModal, (state, action) => {
      if (state.id == action.payload) {
        state.id = 0;
        state.data = undefined;
      }
    });
    builder.addCase(editReportTemplate.fulfilled, (state, action) => {
      if (state.id == action.meta.arg.id) {
        state.id = 0;
        state.data = undefined;
      }
    });
  },
});

export const { resetReportData } = reportDataSlice.actions;
export default reportDataSlice.reducer;
